import React, { useState, useEffect } from 'react';
import { getLeads, getLeadStats, getCategories, getBrands, updateLeadStatus, deleteLead } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trash2, Mail, Phone, Building2, User, MessageSquare, Search, ExternalLink, Filter } from 'lucide-react';

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [buyerTypeFilter, setBuyerTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = async () => {
    try {
      const [leadsRes, statsRes, catsRes, brandsRes] = await Promise.all([
        getLeads(),
        getLeadStats(),
        getCategories(),
        getBrands(),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
      setCategories(catsRes.data);
      setBrands(brandsRes.data);
    } catch (error) {
      toast.error('Failed to fetch leads data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleStatusChange = async (id, status) => {
    try {
      await updateLeadStatus(id, status);
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await deleteLead(id);
      toast.success('Lead deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  // Get names from IDs
  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || null;
  const getBrandName = (id) => brands.find(b => b.id === id)?.name || null;

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    if (statusFilter !== 'all' && lead.status !== statusFilter) return false;
    if (buyerTypeFilter !== 'all' && lead.buyer_type !== buyerTypeFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        lead.name?.toLowerCase().includes(query) ||
        lead.company?.toLowerCase().includes(query) ||
        lead.email?.toLowerCase().includes(query) ||
        lead.phone?.includes(query)
      );
    }
    return true;
  });

  const statusColors = {
    new: 'bg-green-100 text-green-700',
    contacted: 'bg-blue-100 text-blue-700',
    qualified: 'bg-indigo-100 text-indigo-700',
    converted: 'bg-purple-100 text-purple-700',
    closed: 'bg-gray-100 text-gray-700',
  };

  const buyerTypes = ['Retailer', 'Wholesaler', 'Reseller', 'Corporate', 'Export'];

  return (
    <div className="space-y-6" data-testid="admin-leads">
      {/* Header with Stats */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-serif text-[#0B1F3B]">Leads Dashboard</h1>
          <p className="text-gray-500">Manage and track customer inquiries</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white border border-gray-100 p-4">
            <div className="text-2xl font-bold text-[#0B1F3B]">{stats.total}</div>
            <div className="text-sm text-gray-500">Total Leads</div>
          </div>
          <div className="bg-green-50 border border-green-100 p-4">
            <div className="text-2xl font-bold text-green-700">{stats.by_status?.new || 0}</div>
            <div className="text-sm text-green-600">New</div>
          </div>
          <div className="bg-blue-50 border border-blue-100 p-4">
            <div className="text-2xl font-bold text-blue-700">{stats.by_status?.contacted || 0}</div>
            <div className="text-sm text-blue-600">Contacted</div>
          </div>
          <div className="bg-indigo-50 border border-indigo-100 p-4">
            <div className="text-2xl font-bold text-indigo-700">{stats.by_status?.qualified || 0}</div>
            <div className="text-sm text-indigo-600">Qualified</div>
          </div>
          <div className="bg-purple-50 border border-purple-100 p-4">
            <div className="text-2xl font-bold text-purple-700">{stats.by_status?.converted || 0}</div>
            <div className="text-sm text-purple-600">Converted</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-100 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <Filter className="h-4 w-4 text-gray-400" />
          <div className="relative flex-1 max-w-xs">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, phone..."
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="qualified">Qualified</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={buyerTypeFilter} onValueChange={setBuyerTypeFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Buyer Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {buyerTypes.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-500">
            Showing {filteredLeads.length} of {leads.length} leads
          </span>
        </div>
      </div>

      {/* Leads List */}
      {loading ? (
        <div className="bg-white p-8 text-center text-gray-500">Loading...</div>
      ) : filteredLeads.length > 0 ? (
        <div className="space-y-4">
          {filteredLeads.map((lead) => (
            <div key={lead.id} className="bg-white border border-gray-100 p-6" data-testid={`lead-${lead.id}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-medium text-lg text-[#0B1F3B] flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    {lead.name}
                    {lead.buyer_type && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                        {lead.buyer_type}
                      </span>
                    )}
                  </h3>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                    {lead.company && (
                      <span className="flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {lead.company}
                      </span>
                    )}
                    <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-[#D4AF37]">
                      <Mail className="h-3 w-3" />
                      {lead.email}
                    </a>
                    <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-[#D4AF37]">
                      <Phone className="h-3 w-3" />
                      {lead.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={lead.status} onValueChange={(val) => handleStatusChange(lead.id, val)}>
                    <SelectTrigger className={`w-32 ${statusColors[lead.status]}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="qualified">Qualified</SelectItem>
                      <SelectItem value="converted">Converted</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <button onClick={() => handleDelete(lead.id)} className="p-2 text-gray-400 hover:text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {lead.message && (
                <div className="bg-gray-50 p-4 mb-4 rounded">
                  <div className="flex items-start gap-2">
                    <MessageSquare className="h-4 w-4 text-gray-400 mt-0.5" />
                    <p className="text-gray-600 text-sm">{lead.message}</p>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                {lead.interest_category_id && (
                  <span>Interest Category: <span className="text-gray-600">{getCategoryName(lead.interest_category_id) || '-'}</span></span>
                )}
                {lead.interest_brand_id && (
                  <span>Interest Brand: <span className="text-gray-600">{getBrandName(lead.interest_brand_id) || '-'}</span></span>
                )}
                {lead.source_page_url && (
                  <span className="flex items-center gap-1">
                    Source: 
                    <a href={lead.source_page_url} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#D4AF37] flex items-center gap-0.5">
                      {lead.source_page_url.replace(/^https?:\/\/[^/]+/, '').slice(0, 30)}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </span>
                )}
                <span>Date: <span className="text-gray-600">{formatDate(lead.created_at)}</span></span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white p-12 text-center text-gray-500">
          <p>No leads found matching your filters.</p>
        </div>
      )}
    </div>
  );
}
